import { set_dto_checkout_data_from_calculation_service } from "../classes/dto-setters/dto-setter";
import { dto_selected_final_merchant_or_product } from "../classes/dto-selected-merchant-or-product";
import { api_getDeliveryChargeForMIDByZone } from "../connection/api/api-cart";
import { ENTRY_MODE, STORE_MERCHANT } from "../database/store-names";
import { dto_cart_checkout } from "../classes/dto-cart-checkout";
import { ENCRYPT_STORAGE } from "../config/config";
import { dto_Merchant } from "../classes/dto-merchant";
import { dto_common } from "../classes/dto-common";

const PACKING_PRODUCT_ID = 1224929;

export const shouldUseLocalSalesOrderCalculation = () => dto_common.CSP === "DT" && ENCRYPT_STORAGE.getItem(ENTRY_MODE) === "QR";

const shouldLoadZoneDeliveryChargeForLocalCalculation = (merchant) =>
  shouldUseLocalSalesOrderCalculation() &&
  Number(dto_common.DineType) === 4 &&
  !normalizeBoolean(merchant?.IDT) &&
  merchant !== null &&
  merchant !== undefined;

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toFixedAmount = (value) => toNumber(value).toFixed(2);

const normalizeBoolean = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const tokenizeFormula = (formula) => {
  const matches = String(formula ?? "")
    .replace(/SUM/gi, String(sumPlaceholder))
    .match(/\d+(?:\.\d+)?|[()+\-*/]/g);

  return matches || [];
};

const sumPlaceholder = 987654321.123456;

const parseFormulaValue = (tokens, state) => {
  const token = tokens[state.index];
  if (token === "(") {
    state.index += 1;
    const value = parseFormulaExpression(tokens, state);
    if (tokens[state.index] === ")") {
      state.index += 1;
    }
    return value;
  }

  if (token === "-") {
    state.index += 1;
    return -parseFormulaValue(tokens, state);
  }

  state.index += 1;
  return toNumber(token);
};

const parseFormulaTerm = (tokens, state) => {
  let value = parseFormulaValue(tokens, state);

  while (tokens[state.index] === "*" || tokens[state.index] === "/") {
    const operator = tokens[state.index];
    state.index += 1;
    const nextValue = parseFormulaValue(tokens, state);
    value = operator === "*" ? value * nextValue : value / nextValue;
  }

  return value;
};

const parseFormulaExpression = (tokens, state) => {
  let value = parseFormulaTerm(tokens, state);

  while (tokens[state.index] === "+" || tokens[state.index] === "-") {
    const operator = tokens[state.index];
    state.index += 1;
    const nextValue = parseFormulaTerm(tokens, state);
    value = operator === "+" ? value + nextValue : value - nextValue;
  }

  return value;
};

const calculateFormula = (formula, sum) => {
  const tokens = tokenizeFormula(formula).map((token) =>
    token === String(sumPlaceholder) ? String(sum) : token,
  );
  if (tokens.length === 0) {
    return 0;
  }

  try {
    const state = { index: 0 };
    const result = parseFormulaExpression(tokens, state);
    return Number.isFinite(result) ? result : 0;
  } catch (error) {
    return 0;
  }
};

const parseMerchantDeliveryZones = (merchantDeliveryZones) => {
  if (Array.isArray(merchantDeliveryZones)) {
    return merchantDeliveryZones;
  }

  if (typeof merchantDeliveryZones !== "string") {
    return [];
  }

  const trimmed = merchantDeliveryZones.trim();
  if (trimmed === "") {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const parseApiResponsePayload = (response) => {
  if (Array.isArray(response) || (response && typeof response === "object")) {
    return response;
  }

  const rawResponse =
    typeof response === "string" ? response.trim() : String(response ?? "");
  if (rawResponse === "") {
    return null;
  }

  try {
    const parsedResponse = JSON.parse(rawResponse);
    if (typeof parsedResponse === "string") {
      return parseApiResponsePayload(parsedResponse);
    }
    return parsedResponse;
  } catch (error) {
    return null;
  }
};

const extractDeliveryChargeFromPayload = (payload) => {
  const preferredKeys = [
    "DeliveryCharges",
    "EstDeliveryCharges",
    "DeliveryCharge",
    "Charge",
    "Price",
    "UP",
    "Total",
  ];

  const visited = new Set();
  const inspectValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") {
        return null;
      }

      const parsed = Number.parseFloat(trimmed.replace(/[^0-9.-]+/g, ""));
      return Number.isFinite(parsed) ? parsed : null;
    }

    if (typeof value !== "object" || visited.has(value)) {
      return null;
    }

    visited.add(value);

    for (const key of preferredKeys) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const parsed = inspectValue(value[key]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        const parsed = inspectValue(entry);
        if (parsed !== null) {
          return parsed;
        }
      }
      return null;
    }

    for (const entry of Object.values(value)) {
      const parsed = inspectValue(entry);
      if (parsed !== null) {
        return parsed;
      }
    }

    return null;
  };

  return inspectValue(payload);
};

const loadMerchantWithZoneDeliveryCharge = async (merchant) => {
  if (!shouldLoadZoneDeliveryChargeForLocalCalculation(merchant)) {
    return merchant;
  }

  try {
    const response = await api_getDeliveryChargeForMIDByZone();
    const payload = parseApiResponsePayload(response);
    const deliveryCharge = extractDeliveryChargeFromPayload(payload);

    if (deliveryCharge === null) {
      return merchant;
    }

    return {
      ...merchant,
      EstDeliveryCharges: deliveryCharge,
    };
  } catch (error) {
    return merchant;
  }
};

const resolveDeliveryCharge = (merchant) => {
  if (Number(dto_common.DineType) !== 4) {
    return 0;
  }

  if (normalizeBoolean(merchant?.IDT)) {
    return 0;
  }

  const deliveryZoneId = Number(dto_common.DeliveryZoneID);
  const merchantZones = parseMerchantDeliveryZones(merchant?.MerchantDeliveryZones);
  if (merchantZones.length > 0 && Number.isFinite(deliveryZoneId)) {
    const matchedZone = merchantZones.find((zone) => {
      const candidates = [
        zone?.CustomerZoneID,
        zone?.DeliveryZoneID,
        zone?.ZoneID,
        zone?.ZI,
      ].map((value) => Number(value));

      return candidates.includes(deliveryZoneId);
    });

    if (matchedZone) {
      const zoneCharge = toNumber(
        matchedZone.UP ??
          matchedZone.DeliveryCharges ??
          matchedZone.EstDeliveryCharges ??
          matchedZone.Price,
      );
      if (zoneCharge > 0) {
        return zoneCharge;
      }
    }
  }

  return toNumber(merchant?.EstDeliveryCharges);
};

const resolveDeliveryTaxEquation = () =>
  dto_common.NatvieCountry === "MV" ? "SUM * 8/100" : "SUM * 0/100";

const resolveLineTaxEquation = (item) => {
  if (Number(item?.TID) === 5) {
    return resolveDeliveryTaxEquation();
  }

  return item?.TaxEquation || item?.TE || "SUM * 0/100";
};

const resolveLineTaxId = (item) => {
  if (Number(item?.TID) === 5) {
    return dto_common.NatvieCountry === "MV" ? 2 : 1;
  }

  return Number(item?.TAXID) || 1;
};

const resolveLineDecPoint = (item) => item?.DecPoint || item?.DP || "n0";

const buildPackingItem = (merchant, nextLineId) => ({
  LineID: nextLineId,
  PID: PACKING_PRODUCT_ID,
  TID: 1,
  Code: "15",
  ProductImageUrl: "",
  Desc: "PACKING",
  USeqID: 1224930,
  UOM: "EACH",
  UP: toNumber(merchant?.PackingCharge),
  UD: 0,
  Qty: 1,
  DecPoint: "n0",
  Total: 0,
  Modification: "",
  ExtraCost: 0,
  DocDiscount: 0,
  VAT: 0,
  TAXID: 1,
  TaxEquation: "SUM * 0/100",
  MID: Number(merchant?.MID) || 0,
  GDescription: "",
  IsAvailable: true,
  ADDONLINEID: 0,
  UDPer: 0,
  IsRecommended: 0,
});

const buildDeliveryItem = (merchant, deliveryCharge, nextLineId) => ({
  LineID: nextLineId,
  PID: 73163,
  TID: 5,
  Code: "D003",
  ProductImageUrl: "",
  Desc: "DELIVERY",
  USeqID: 73164,
  UOM: "EACH",
  UP: deliveryCharge,
  UD: 0,
  Qty: 1,
  DecPoint: "n0",
  Total: 0,
  Modification: "",
  ExtraCost: 0,
  DocDiscount: 0,
  VAT: 0,
  TAXID: resolveLineTaxId({ TID: 5 }),
  TaxEquation: resolveDeliveryTaxEquation(),
  MID: Number(merchant?.MID) || 0,
  GDescription: "",
  IsAvailable: true,
  ADDONLINEID: 0,
  UDPer: 0,
  IsRecommended: 0,
});

export const calculateSalesOrderLocal = ({
  cart,
  merchant,
  isChangePromo = false,
  customerPromoCount = 0,
}) => {
  const workingCart = Array.isArray(cart)
    ? cart.map((item) => ({ ...item }))
    : [];
  const serviceChargePer = toNumber(merchant?.ServiceChargePer);
  const packingCharge = toNumber(merchant?.PackingCharge);
  const deliveryCharge = resolveDeliveryCharge(merchant);
  const isDeliveryHandledByMerchant = normalizeBoolean(merchant?.IDT);

  const hasPackingItem = workingCart.some(
    (item) => Number(item?.PID) === PACKING_PRODUCT_ID,
  );
  if (
    (dto_common.CSP === "AV" || dto_common.CSP === "LG") &&
    packingCharge > 0 &&
    !hasPackingItem
  ) {
    workingCart.push(buildPackingItem(merchant, workingCart.length + 1));
  }

  const existingDeliveryIndex = workingCart.findIndex(
    (item) => Number(item?.TID) === 5,
  );
  if (Number(dto_common.DineType) === 4) {
    const deliveryItem = buildDeliveryItem(
      merchant,
      deliveryCharge,
      existingDeliveryIndex >= 0
        ? Number(workingCart[existingDeliveryIndex]?.LineID) || workingCart.length + 1
        : workingCart.length + 1,
    );

    if (existingDeliveryIndex >= 0) {
      workingCart[existingDeliveryIndex] = {
        ...workingCart[existingDeliveryIndex],
        ...deliveryItem,
      };
    } else {
      workingCart.push(deliveryItem);
    }
  } else if (existingDeliveryIndex >= 0) {
    workingCart.splice(existingDeliveryIndex, 1);
  }

  let runningSubTotal = 0;
  let rowDiscountTotal = 0;
  workingCart.forEach((item, index) => {
    const unitPrice = toNumber(item.UP);
    const unitDiscount = toNumber(item.UD);
    const quantity = toNumber(item.Qty);
    const extraCost = toNumber(item.ExtraCost);
    const lineSubTotal = (unitPrice - unitDiscount) * quantity + extraCost;

    item.LineID = index + 1;
    item.UP = unitPrice;
    item.UD = unitDiscount;
    item.Qty = quantity;
    item.ExtraCost = extraCost;
    item.DecPoint = resolveLineDecPoint(item);
    item.Total = toFixedAmount(lineSubTotal);
    item.UDPer = unitPrice > 0 && Number(item.TID) !== 5
      ? toFixedAmount((unitDiscount * 100) / unitPrice)
      : "0.00";
    item.TAXID = resolveLineTaxId(item);
    item.TaxEquation = resolveLineTaxEquation(item);

    runningSubTotal += lineSubTotal;
    rowDiscountTotal += unitDiscount * quantity;
  });

  const totalForDocDiscountBase = Math.max(runningSubTotal - deliveryCharge, 0);
  let discountPercentage = isChangePromo
    ? 0
    : toNumber(dto_cart_checkout.DiscountPersentage);
  let docDiscountTotal = isChangePromo ? 0 : toNumber(dto_cart_checkout.DiscountTotal);
  let promoGuid = isChangePromo ? 0 : Number(dto_cart_checkout.PromoGuid) || 0;
  let promoCode = isChangePromo ? "" : dto_cart_checkout.PromoCODE || "";
  let promoHeader = isChangePromo ? "" : dto_cart_checkout.PromoHeader || "";

  if (discountPercentage > 0 && totalForDocDiscountBase > 0) {
    docDiscountTotal = (discountPercentage * totalForDocDiscountBase) / 100;
  } else if (docDiscountTotal > 0 && totalForDocDiscountBase > 0) {
    discountPercentage = (docDiscountTotal * 100) / totalForDocDiscountBase;
  }

  if (docDiscountTotal > totalForDocDiscountBase) {
    docDiscountTotal = 0;
    discountPercentage = 0;
    promoGuid = 0;
    promoCode = "";
    promoHeader = "";
  }

  let totalTax = 0;
  let totalServiceCharge = 0;
  const xmlTaxDoc = [];

  workingCart.forEach((item) => {
    const lineSubTotal =
      (toNumber(item.UP) - toNumber(item.UD)) * toNumber(item.Qty) +
      toNumber(item.ExtraCost);
    const applyDocDiscount = Number(item.TID) !== 5 && totalForDocDiscountBase > 0;
    const lineDocDiscount = applyDocDiscount
      ? (docDiscountTotal / totalForDocDiscountBase) * lineSubTotal
      : 0;
    const lineServiceCharge =
      Number(item.TID) !== 5
        ? ((lineSubTotal - lineDocDiscount) * serviceChargePer) / 100
        : 0;
    const lineTaxBase = lineSubTotal - lineDocDiscount + lineServiceCharge;
    const lineTax = calculateFormula(item.TaxEquation, lineTaxBase);

    item.DocDiscount = lineDocDiscount.toFixed(6);
    item.ServiceCharge = lineServiceCharge.toFixed(6);
    item.VAT = lineTax.toFixed(2);
    item.Total = lineSubTotal.toFixed(2);

    totalServiceCharge += lineServiceCharge;
    totalTax += lineTax;
    xmlTaxDoc.push({
      POSLineID: item.LineID,
      TaxLineID: 1,
      DetailTaxId: item.TAXID,
      Equation: item.TaxEquation,
      TaxCalTotal: lineTax.toFixed(2),
    });
  });

  const displayDiscount = docDiscountTotal + rowDiscountTotal;
  const nonDisplaySubTotal = runningSubTotal + rowDiscountTotal;
  const displaySubtotal = Math.max(nonDisplaySubTotal - deliveryCharge, 0);
  const subTotal = runningSubTotal + rowDiscountTotal - deliveryCharge;
  const grandTotal =
    subTotal -
    rowDiscountTotal -
    docDiscountTotal +
    totalServiceCharge +
    totalTax +
    deliveryCharge;

  const minOrderValue =
    Number(dto_common.DineType) === 4
      ? toNumber(merchant?.MB ?? merchant?.MinBillValue)
      : 0;
  const maxOrderValue =
    Number(dto_common.DineType) === 4
      ? toNumber(merchant?.MX ?? merchant?.MaxBillValue)
      : 0;

  const merchantDineType = Number(merchant?.DineType);
  const selectedDineType = Number(dto_common.DineType);

  return [
    [workingCart],
    { GrandTotal: toFixedAmount(grandTotal) },
    { SubTotal: toFixedAmount(subTotal) },
    { RowDiscountTotal: toFixedAmount(rowDiscountTotal) },
    { TotalTax: toFixedAmount(totalTax) },
    { TotalServiceCharge: toFixedAmount(totalServiceCharge) },
    { DiscountPercentage: toFixedAmount(discountPercentage) },
    { DocDiscountTotal: toFixedAmount(docDiscountTotal) },
    { DeliveryCharges: toFixedAmount(deliveryCharge) },
    { MinOrderValue: toFixedAmount(minOrderValue) },
    { MaxOrderValue: toFixedAmount(maxOrderValue) },
    { NonDisplaySubTotal: toFixedAmount(nonDisplaySubTotal) },
    { DisplayDiscount: toFixedAmount(displayDiscount) },
    { PromoGUID: String(docDiscountTotal > 0 ? promoGuid : 0) },
    { PromoCode: docDiscountTotal > 0 ? promoCode : "" },
    { XMLTaxDoc: [xmlTaxDoc] },
    { Error: normalizeBoolean(merchant?.IO) === false ? "SORRY MERCHANT CLOSED..!" : "" },
    { PayMode: merchant?.PM || merchant?.PayMode || dto_cart_checkout.PayModeTypes || "" },
    {
      BankTransferAccountDetails:
        merchant?.BTA ||
        merchant?.BankTransferAccountDetails ||
        dto_cart_checkout.BankTransferAccountDetails ||
        "",
    },
    { ProductPreparationError: "" },
    { ScheduleProductPrepareTime: "0" },
    { IPGUrl: merchant?.IPGUrl || dto_cart_checkout.IPGUrl || "" },
    { IPGUsername: merchant?.IPGUsername || dto_cart_checkout.IPGUsername || "" },
    { IPGPassword: merchant?.IPGPassword || dto_cart_checkout.IPGPassword || "" },
    {
      IPGMerchantNumber:
        merchant?.IPGMerchantNumber || dto_cart_checkout.IPGMerchantNumber || "",
    },
    { CustomerPromoCount: String(customerPromoCount || 0) },
    { isDeliverChargeApplyByMerchant: String(isDeliveryHandledByMerchant) },
    {
      MerchantDineType: String(
        Number.isFinite(selectedDineType) && selectedDineType > 0
          ? selectedDineType
          : Number.isFinite(merchantDineType) && merchantDineType > 0
            ? merchantDineType
            : 0,
      ),
    },
    { PromoHeader: docDiscountTotal > 0 ? promoHeader : "" },
    { DiscountDeliveryCharges: "0.00" },
    { DisplaySubtotal: toFixedAmount(displaySubtotal) },
  ];
};

export const applyLocalSalesOrderCalculation = async ({
  cart,
  isChangePromo = false,
  customerPromoCount = 0,
}) => {
  if (!shouldUseLocalSalesOrderCalculation()) {
    return null;
  }

  const merchants = ENCRYPT_STORAGE.getItem(STORE_MERCHANT) || [];
  const selectedMerchant =
    merchants.find &&
    merchants.find(
      (merchant) =>
        parseInt(merchant.MID) ===
        parseInt(dto_selected_final_merchant_or_product.MID),
    );
  const merchantWithZoneCharge = await loadMerchantWithZoneDeliveryCharge(
    selectedMerchant || dto_Merchant || {},
  );

  const calculation = calculateSalesOrderLocal({
    cart,
    merchant: merchantWithZoneCharge,
    isChangePromo,
    customerPromoCount,
  });

  await set_dto_checkout_data_from_calculation_service(calculation);
  return calculation;
};

export const calculateSalesOrderLocalWithDeliveryCharge = async ({
  cart,
  merchant,
  isChangePromo = false,
  customerPromoCount = 0,
}) => {
  const merchantWithZoneCharge = await loadMerchantWithZoneDeliveryCharge(
    merchant || dto_Merchant || {},
  );

  return calculateSalesOrderLocal({
    cart,
    merchant: merchantWithZoneCharge,
    isChangePromo,
    customerPromoCount,
  });
};
